
import React, { useRef, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import MarkdownRenderer from "@/components/MarkdownRenderer";

interface CampaignPlannerSectionProps {
  registerSectionRef: (id: string, element: HTMLElement | null) => void;
}

const overviewMarkdown = `
The Campaign Planner is an AI-powered tool that helps you create full multi-channel marketing campaigns in minutes. It generates a strategic brief and ready-to-use assets across multiple channels:

- **Email** – Subject lines and body copy
- **Social Media** – Posts for LinkedIn, Twitter/X, Facebook, Instagram
- **Ads** – Google Ads, Facebook/Instagram Ads copy
- **SMS** – Short promotional messages
- **Landing Pages** – Hero copy, CTAs, and supporting content
`;

const creatingCampaignMarkdown = `
Creating a campaign follows a guided 3-step wizard:

### Step 1 — Define Your Goal
Enter a description of your campaign idea (e.g., "St. Patrick's Day promotion for our Irish pub"). You can also pick from a gallery of campaign templates to get started faster.

### Step 2 — Review the Strategic Brief
The AI generates a strategic brief including:
- **Campaign Name** and **Tagline**
- **Target Audience** description
- **Key Messages** and talking points
- **Timeline** with specific start, peak, and end dates
- **Success Metrics** to measure performance

You can regenerate the brief or edit it before proceeding.

### Step 3 — Generate & Edit Assets
The AI creates assets for each channel. You can:
- Switch between channels using the tabbed interface
- Adjust the **Tone Slider** (Professional ↔ Casual)
- Regenerate individual assets
- Copy content to clipboard
- Export all assets
`;

const managingAssetsMarkdown = `
Each generated asset respects platform-specific character limits:

| Channel | Max Characters |
|---------|---------------|
| Twitter/X | 280 |
| SMS | 160 |
| Google Ads Headline | 30 |
| Google Ads Description | 90 |
| Email Subject | 100 |

Use the **tone slider** to shift the voice of your content between professional and casual. Regenerating an asset preserves your tone preference.
`;

const dashboardMarkdown = `
The Campaign Dashboard at \`/campaign-planner\` shows all your campaigns in one place:

- **Status indicators** — Draft, Active, or Completed
- **Quick actions** — Edit, duplicate, or delete campaigns
- **Create new** — Launch the wizard from the dashboard

Campaigns are scoped to your current company, so switching companies shows a different set of campaigns.
`;

const CampaignPlannerSection = ({ registerSectionRef }: CampaignPlannerSectionProps) => {
  const overviewRef = useRef<HTMLHeadingElement>(null);
  const creatingRef = useRef<HTMLHeadingElement>(null);
  const assetsRef = useRef<HTMLHeadingElement>(null);
  const dashboardRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    registerSectionRef('campaign-overview', overviewRef.current);
    registerSectionRef('creating-campaign', creatingRef.current);
    registerSectionRef('managing-assets', assetsRef.current);
    registerSectionRef('campaign-dashboard', dashboardRef.current);
  }, [registerSectionRef]);

  return (
    <>
      <h2
        id="campaign-overview"
        ref={overviewRef}
        className="text-2xl font-bold tracking-tight scroll-mt-16"
      >
        Campaign Planner Overview
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>AI-Powered Campaign Generation</CardTitle>
          <CardDescription>Create multi-channel marketing campaigns with AI assistance</CardDescription>
        </CardHeader>
        <CardContent>
          <MarkdownRenderer content={overviewMarkdown} />
        </CardContent>
      </Card>

      <h2
        id="creating-campaign"
        ref={creatingRef}
        className="text-2xl font-bold tracking-tight scroll-mt-16 mt-8"
      >
        Creating a Campaign
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>3-Step Campaign Wizard</CardTitle>
          <CardDescription>From idea to ready-to-publish assets in minutes</CardDescription>
        </CardHeader>
        <CardContent>
          <MarkdownRenderer content={creatingCampaignMarkdown} />
        </CardContent>
      </Card>

      <h2
        id="managing-assets"
        ref={assetsRef}
        className="text-2xl font-bold tracking-tight scroll-mt-16 mt-8"
      >
        Managing Assets
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Asset Editor</CardTitle>
          <CardDescription>Fine-tune generated content for each channel</CardDescription>
        </CardHeader>
        <CardContent>
          <MarkdownRenderer content={managingAssetsMarkdown} />
        </CardContent>
      </Card>

      <h2
        id="campaign-dashboard"
        ref={dashboardRef}
        className="text-2xl font-bold tracking-tight scroll-mt-16 mt-8"
      >
        Campaign Dashboard
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Managing Your Campaigns</CardTitle>
          <CardDescription>View, edit, and track all your campaigns</CardDescription>
        </CardHeader>
        <CardContent>
          <MarkdownRenderer content={dashboardMarkdown} />
        </CardContent>
      </Card>
    </>
  );
};

export default CampaignPlannerSection;
